import { JSONSchemaType } from "ajv";
import { watch, ref, reactive, toRefs } from "vue";
import { defineStore } from "pinia";
import { delayedCall } from "@/utils";
import { KeyCode, ConsumerKey } from "@/keyboard";
import { LocalStorage } from "@/localStorage";

const MODS = [
  "LCtrl",
  "LShift",
  "LAlt",
  "LGui",
  "RCtrl",
  "RShift",
  "RAlt",
  "RGui",
];

export const isModifier = (kc: KeyCode): boolean => {
  return MODS.includes(kc);
};

export interface Icon {
  icon: string;
}

// List of keys displayed as text and the ones displayed as icons
export interface KeyIconDisplay {
  text: string[];
  icon: Icon[];
}

// Display of keys split into modifier keys and others
export interface KeyModDisplay {
  keys: KeyIconDisplay;
  mods: KeyIconDisplay;
}

const getOs = () => {
  const byPatterns = (patterns: string[]) => {
    // possible platforms: https://stackoverflow.com/a/19883965
    return patterns.some((p) => navigator.platform.indexOf(p) != -1);
  };

  if (byPatterns(["Mac"])) {
    return "mac";
  } else if (byPatterns(["Win"])) {
    return "windows";
  } else if (byPatterns(["Linux"])) {
    return "linux";
  }

  return "";
};

const _icon = (name: string) => ({ icon: name });

export const TRANS_ICON = _icon("mdi:arrow-down-left");

const KEY_ICONS: { [key: string]: Icon | undefined } = {
  LShift: _icon("mdi:apple-keyboard-shift"),
  RShift: _icon("mdi:apple-keyboard-shift"),
  LCtrl: _icon("mdi:apple-keyboard-control"),
  RCtrl: _icon("mdi:apple-keyboard-control"),
  LAlt: _icon("mdi:apple-keyboard-option"),
  RAlt: _icon("mdi:apple-keyboard-option"),
  Tab: _icon("mdi:keyboard-tab"),
  BSpace: _icon("mdi:backspace"),
  Space: _icon("mdi:keyboard-space"),
  CapsLock: _icon("mdi:keyboard-caps"),
  Enter: _icon("mdi:keyboard-return"),
  Left: _icon("mdi:arrow-left"),
  Right: _icon("mdi:arrow-right"),
  Up: _icon("mdi:arrow-up"),
  Down: _icon("mdi:arrow-down"),
  Trans: TRANS_ICON,
};

const getOsIcon = (os: string) => {
  return os == "mac"
    ? _icon("mdi:apple-keyboard-command")
    : os == "windows"
    ? _icon("mdi:microsoft-windows")
    : os == "linux"
    ? _icon("mdi:linux")
    : _icon("ph:diamond-fill");
};

const DISPLAY: Partial<Record<KeyCode, string>> = {
  Kb1: "1",
  Kb2: "2",
  Kb3: "3",
  Kb4: "4",
  Kb5: "5",
  Kb6: "6",
  Kb7: "7",
  Kb8: "8",
  Kb9: "9",
  Kb0: "0",

  Minus: "-",
  Equal: "=",
  LBracket: "[",
  RBracket: "]",
  Bslash: "\\",
  NonUsHash: "#",
  SColon: ";",
  Quote: '"',
  Grave: "`",
  Comma: ",",
  Dot: ".",
  Slash: "/",
  NonUsBslash: "\\",

  KpSlash: "/",
  KpAsterisk: "*",
  KpMinus: "-",
  KpPlus: "+",
  KpEnter: "Enter",
  Kp1: "1",
  Kp2: "2",
  Kp3: "3",
  Kp4: "4",
  Kp5: "5",
  Kp6: "6",
  Kp7: "7",
  Kp8: "8",
  Kp9: "9",
  Kp0: "0",
  KpDot: ".",
  KpEqual: "=",
  KpComma: ",",
  KpEqualSign: "=",

  LCtrl: "Left Ctrl",
  LShift: "Left Shift",
  LAlt: "Left Alt",
  LGui: "Left Gui",
  RCtrl: "Right Ctrl",
  RShift: "Right Shift",
  RAlt: "Right Alt",
  RGui: "Right Gui",

  Escape: "Escape",
  BSpace: "Backspace",

  CapsLock: "Caps Lock",
  PScreen: "Print Screen",
  PgUp: "Page Up",
  PgDown: "Page Down",
  NumLock: "Num Lock",

  VolUp: "Volume Up",
  VolDown: "Volume Down",
  LockingCapsLock: "Locking Caps Lock",
  LockingNumLock: "Locking NumLock",
  LockingScrollLock: "Locking ScrollLock",

  MediaPlayPause: "Play/​Pause", // zero-width space after "/" to allow word-breaking
  MediaStopCD: "Stop CD",
  MediaPreviousSong: "Previous Song",
  MediaNextSong: "Next Song",
  MediaEjectCD: "Eject CD",
  MediaVolUp: "Volume Up",
  MediaVolDown: "Volume Down",
  MediaMute: "Mute",
  MediaWWW: "WWW",
  MediaBack: "Back",
  MediaForward: "Forward",
  MediaStop: "Stop",
  MediaFind: "Find",
  MediaScrollUp: "Scroll Up",
  MediaScrollDown: "Scroll Down",
  MediaEdit: "Edit",
  MediaSleep: "Sleep",
  MediaCoffee: "Coffee",
  MediaRefresh: "Refresh",
  MediaCalc: "Calc",
};

const DISPLAY_SHORT: Partial<Record<KeyCode, string>> = {
  LCtrl: "Ctrl",
  LShift: "Shift",
  LAlt: "Alt",
  LGui: "Gui", // TODO: different names depending on operating system
  RCtrl: "RCtrl",
  RShift: "RShift",
  RAlt: "RAlt",
  RGui: "RGui",

  CapsLock: "CapsLck",
  PScreen: "PrtSc",
  ScrollLock: "ScrLck",
  Insert: "Ins",
  Delete: "Del",
  PgUp: "PgUp",
  PgDown: "PgDn",
  NumLock: "Num",
  Escape: "Esc",
  BSpace: "BSpace",

  Application: "App",
  Execute: "Exec",
  VolUp: "Vol+",
  VolDown: "Vol-",
  LockingCapsLock: "LckCapsLck",
  LockingNumLock: "LckNum",
  LockingScrollLock: "LckScrLck",

  MediaPlayPause: "Play",
  MediaStopCD: "StopCD",
  MediaPreviousSong: "Prev",
  MediaNextSong: "Next",
  MediaEjectCD: "Eject",
  MediaVolUp: "Vol+",
  MediaVolDown: "Vol-",
  MediaMute: "Mute",
  MediaWWW: "WWW",
  MediaBack: "Back",
  MediaForward: "Fwd",
  MediaStop: "Stop",
  MediaFind: "Find",
  MediaScrollUp: "ScrUp",
  MediaScrollDown: "ScrDn",
  MediaEdit: "Edit",
  MediaSleep: "Sleep",
  MediaCoffee: "Coffee",
  MediaRefresh: "Refresh",
  MediaCalc: "Calc",
};

export const DISPLAY_SHIFTED: { [key: string]: string } = {
  Kb1: "!",
  Kb2: "@",
  Kb3: "#",
  Kb4: "$",
  Kb5: "%",
  Kb6: "^",
  Kb7: "&",
  Kb8: "*",
  Kb9: "(",
  Kb0: ")",
  Minus: "_",
  Equal: "+",
  LBracket: "{",
  RBracket: "}",
  Bslash: "|",
  NonUsHash: "~",
  SColon: ":",
  Quote: '"',
  Grave: "~",
  Comma: "<",
  Dot: ">",
  Slash: "?",
  NonUsBslash: "|",
};

export const DISPLAY_CONSUMER: Partial<Record<ConsumerKey, string>> = {
  ScanPreviousTrack: "Previous Song",
  ScanNextTrack: "Next Song",
  PlayPause: "Play/​Pause", // zero-width space after "/" to allow word-breaking
  Stop: "Stop",
  Mute: "Mute",
  VolumeIncrement: "Volume Up",
  VolumeDecrement: "Volume Down",
};

export const DISPLAY_CONSUMER_SHORT: Partial<Record<ConsumerKey, string>> = {
  ScanPreviousTrack: "Prev",
  ScanNextTrack: "Next",
  PlayPause: "Play",
  Stop: "Stop",
  Mute: "Mute",
  VolumeIncrement: "Vol+",
  VolumeDecrement: "Vol-",
};

interface Settings {
  use_icons: boolean;
  use_mac_shift: boolean;
  use_mac_ctrl: boolean;
  use_mac_gui: boolean;
  use_mac_alt: boolean;
  short_names: boolean;
  lowercase: boolean;
  use_hold_tap_icons: boolean;
  show_hold_tap: boolean;
  advanced_consumer_keys: boolean;
}

const SCHEMA: JSONSchemaType<Settings> = {
  type: "object",
  properties: {
    use_icons: { type: "boolean" },
    use_mac_shift: { type: "boolean" },
    use_mac_ctrl: { type: "boolean" },
    use_mac_gui: { type: "boolean" },
    use_mac_alt: { type: "boolean" },
    short_names: { type: "boolean" },
    lowercase: { type: "boolean" },
    use_hold_tap_icons: { type: "boolean" },
    show_hold_tap: { type: "boolean" },
    advanced_consumer_keys: { type: "boolean" },
  },
  required: [],
};

const noop = (v: Settings) => v;
const STORAGE = new LocalStorage("keycap", SCHEMA, noop, noop);

export const useKeycapStore = defineStore("keycap", () => {
  const loaded = STORAGE.get() ?? ({} as Settings);

  const os = getOs();
  const icons_os = ref(os);
  const settings = reactive({
    use_icons: loaded.use_icons ?? true,
    use_mac_shift: loaded.use_mac_shift ?? os == "mac",
    use_mac_ctrl: loaded.use_mac_ctrl ?? os == "mac",
    use_mac_gui: loaded.use_mac_gui ?? os == "mac",
    use_mac_alt: loaded.use_mac_alt ?? os == "mac",
    short_names: loaded.short_names ?? true,
    lowercase: loaded.lowercase ?? false,
    use_hold_tap_icons: loaded.use_hold_tap_icons ?? true,
    show_hold_tap: loaded.show_hold_tap ?? true,
    advanced_consumer_keys: loaded.advanced_consumer_keys ?? false,
    // TODO: gui_name: "Gui" as GuiName,
  });

  // Save to localStorage after settings are modified by the user
  const save = delayedCall(500, () => STORAGE.set({ ...settings }));
  watch(settings, save);

  const display = (kc: KeyCode, shifted: boolean): Icon | string => {
    // Shifted keys have no associated icons anyway
    if (shifted && DISPLAY_SHIFTED[kc]) {
      return DISPLAY_SHIFTED[kc];
    }

    // Resolve conditional icons
    const shift = kc == "LShift" || kc == "RShift";
    const ctrl = kc == "LCtrl" || kc == "RCtrl";
    const alt = kc == "LAlt" || kc == "RAlt";
    const gui = kc == "LGui" || kc == "RGui";
    const use_icon =
      !(shift || ctrl || alt) ||
      (shift && settings.use_mac_shift) ||
      (ctrl && settings.use_mac_ctrl) ||
      (alt && settings.use_mac_alt);
    // Return icon if applicable
    if (settings.use_icons && use_icon) {
      const icon = gui
        ? getOsIcon(settings.use_mac_gui ? "mac" : icons_os.value)
        : KEY_ICONS[kc];
      if (icon) {
        return icon;
      }
    }

    // Prepare display text
    const name =
      settings.short_names && DISPLAY_SHORT[kc]
        ? DISPLAY_SHORT[kc]
        : DISPLAY[kc];
    const text = name || kc.toString();

    return settings.lowercase ? text.toLowerCase() : text;
  };

  const displayMultiple = (keycodes: KeyCode[]): KeyModDisplay => {
    let mods = keycodes.filter((kc) => isModifier(kc));
    const keys = keycodes.filter((kc) => !isModifier(kc));
    const is_shift = (kc: KeyCode) => kc == "LShift" || kc == "RShift";

    // Resolve special case with shifted key code
    const shift_pos = mods.findIndex(is_shift);
    const do_shift =
      keys.length == 1 && keys[0] in DISPLAY_SHIFTED && shift_pos != -1;

    if (do_shift) {
      let shift_consumed = false;
      mods = mods.filter((kc) => {
        if (!shift_consumed && is_shift(kc)) {
          shift_consumed = true;
          return false;
        }
        return true;
      });
    }

    const key_display = keys.map((kc) => display(kc, do_shift));
    const mod_display = mods.map((kc) => display(kc, do_shift));
    if (key_display.length == 0 && mod_display.length == 1) {
      const mod = mod_display.pop();
      if (mod) {
        key_display.push(mod);
      }
    }
    // Typescript...
    const is_icon = (d: Icon | string): d is Icon =>
      (d as Icon).icon != undefined;
    const is_not_icon = (d: Icon | string): d is string => !is_icon(d);

    return {
      mods: {
        text: mod_display.filter(is_not_icon),
        icon: mod_display.filter(is_icon),
      },
      keys: {
        text: key_display.filter(is_not_icon),
        icon: key_display.filter(is_icon),
      },
    };
  };

  const displayConsumer = (key: ConsumerKey): Icon | string => {
    const name =
      settings.short_names && DISPLAY_CONSUMER_SHORT[key]
        ? DISPLAY_CONSUMER_SHORT[key]
        : DISPLAY_CONSUMER[key];
    return name || key;
  };

  return {
    icons_os,
    ...toRefs(settings),
    display,
    displayMultiple,
    displayConsumer,
  };
});
